import { findTerm } from '@engpod/core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/Icon';
import { speak } from '../lib/format';
import { prefs } from '../lib/local';
import { useRecorder } from '../lib/useRecorder';
import type { StepProps } from './context';

type Level = 'easy' | 'guided' | 'free';
const LEVELS: [Level, string, string][] = [
  ['easy', '入门', '用一个表达说一句'],
  ['guided', '进阶', '跟读示范回答'],
  ['free', '挑战', '自己说 30 秒'],
];

/**
 * 输出 in three levels so beginners can take part:
 * 入门 — say one sentence with an expression (optionally swap in your own words),
 * 进阶 — shadow a short model answer, 挑战 — free speaking. Any level completes the step.
 */
export function Output({ detail, state, update, complete }: StepProps) {
  const [level, setLevel] = useState<Level>(() => prefs.get<Level>('outputLevel', 'easy'));
  const choose = (l: Level) => {
    setLevel(l);
    prefs.set('outputLevel', l);
  };
  const allDone = ['gist', 'intensive', 'expressions', 'shadowing'].every((k) => state.steps[k as 'gist']?.done);
  return (
    <div className="step-body output">
      <div className="level-tabs">
        {LEVELS.map(([id, name, sub]) => (
          <button key={id} className={level === id ? 'active' : ''} onClick={() => choose(id)}>
            <b>{name}</b>
            <small>{sub}</small>
          </button>
        ))}
      </div>
      {level === 'easy' && <EasyLevel detail={detail} onDone={() => complete('output')} />}
      {level === 'guided' && <GuidedLevel detail={detail} onDone={() => complete('output')} />}
      {level === 'free' && <FreeLevel detail={detail} state={state} update={update} onDone={() => complete('output')} />}
      {state.steps.output?.done && (
        <div className="done-card">
          <b>{allDone ? '这一集五步全部完成 🎉' : '输出完成'}</b>
          <p>
            明天记得复习今天加入的生词和表达。<Link to="/review">去复习 →</Link>
          </p>
        </div>
      )}
    </div>
  );
}

function RecordRow({ label = '录下来' }: { label?: string }) {
  const rec = useRecorder();
  return (
    <div className="rec-row">
      {rec.state === 'recording' ? (
        <button className="rec-pill on" onClick={rec.stop}>
          ■ 停止 · {rec.seconds}s
        </button>
      ) : (
        <button className="rec-pill" onClick={() => void rec.start()}>
          <Icon name="mic" /> {rec.state === 'ready' ? '重录' : label}
        </button>
      )}
      {rec.state === 'ready' && <button onClick={() => void rec.play()}>▶ 听我的</button>}
      {rec.state === 'denied' && <small className="status">无法使用麦克风</small>}
    </div>
  );
}

function EasyLevel({ detail, onDone }: { detail: StepProps['detail']; onDone(): void }) {
  const items = detail.lesson.expressions.flatMap((x) => (x.examples ?? []).slice(0, 1).map((ex) => ({ text: x.text, meaning: x.meaning, ...ex })));
  const [k, setK] = useState(0);
  const [mine, setMine] = useState('');
  const item = items[k];
  useEffect(() => setMine(item?.en ?? ''), [item]);
  if (!item) return <p className="muted">这一集还没有可用的表达例句，试试「进阶」或「挑战」。</p>;
  const at = findTerm(item.en, item.text);
  return (
    <div className="level-body">
      <p className="level-lead">选一个表达，先听示范，再跟着说一遍。说出来就算完成。</p>
      <div className="chips">
        {items.map((x, n) => (
          <button key={x.text} className={`chip${n === k ? ' on' : ''}`} onClick={() => setK(n)}>
            {x.text}
          </button>
        ))}
      </div>
      <div className="say-card">
        <p className="say-en">
          {at ? (
            <>
              {item.en.slice(0, at.start)}
              <mark>{item.en.slice(at.start, at.end)}</mark>
              {item.en.slice(at.end)}
            </>
          ) : (
            item.en
          )}
        </p>
        <p className="say-zh">{item.zh}</p>
        <div className="row wrap">
          <button onClick={() => speak(item.en)}>
            <Icon name="volume" /> 听示范
          </button>
          <RecordRow label="跟着说" />
        </div>
      </div>
      <label className="field">
        <span>换成你自己的说法（可选）：改几个词再说一遍</span>
        <textarea rows={2} value={mine} onChange={(e) => setMine(e.target.value)} />
      </label>
      <button className="primary" onClick={onDone}>
        我说出来了 ✓
      </button>
    </div>
  );
}

function GuidedLevel({ detail, onDone }: { detail: StepProps['detail']; onDone(): void }) {
  const prompt = detail.lesson.prompt;
  const sample = prompt?.sample ?? [];
  return (
    <div className="level-body">
      <h4>{prompt?.text ?? '用今天学到的表达，说说你自己的经历或看法。'}</h4>
      {sample.length ? (
        <>
          <p className="level-lead">这是一段示范回答。逐句听、跟着说，熟了再连起来说一遍。</p>
          <ol className="sample-list">
            {sample.map((s, n) => (
              <li key={n}>
                <button className="icon plain" aria-label="朗读" onClick={() => speak(s.en)}>
                  <Icon name="volume" />
                </button>
                <div>
                  <p>{s.en}</p>
                  <small>{s.zh}</small>
                </div>
              </li>
            ))}
          </ol>
          <button className="plain small" onClick={() => speak(sample.map((s) => s.en).join(' '))}>
            ▶ 连起来听一遍
          </button>
          <RecordRow label="录下整段" />
        </>
      ) : (
        <p className="muted">这一集还没有示范回答，试试「入门」或「挑战」。</p>
      )}
      <button className="primary" onClick={onDone}>
        完成 ✓
      </button>
    </div>
  );
}

function FreeLevel({ detail, state, update, onDone }: { detail: StepProps['detail']; state: StepProps['state']; update: StepProps['update']; onDone(): void }) {
  const [text, setText] = useState(state.outputText ?? '');
  useEffect(() => {
    const t = setTimeout(() => text !== (state.outputText ?? '') && update({ outputText: text }), 800);
    return () => clearTimeout(t);
  }, [text, state.outputText, update]);
  const prompt = detail.lesson.prompt;
  return (
    <div className="level-body">
      <h4>{prompt?.text ?? '用今天学到的表达，说说你自己的经历或看法。'}</h4>
      {prompt?.hint && <p className="muted">可以这样开头：{prompt.hint}</p>}
      <div className="chips">
        {detail.lesson.expressions.map((e) => (
          <span key={e.id} className="chip">
            {e.text}
          </span>
        ))}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="先写 3–5 句英文草稿（可选），再脱稿说 30 秒" />
      <RecordRow label="录下我的回答" />
      <button className="primary" onClick={onDone}>
        完成 ✓
      </button>
    </div>
  );
}
